/*
 * Rollouts tab (S4/S5): full lifecycle surface for OTA rollouts, rendered as
 * the third tab inside /settings/artifacts. Kept as its own file because the
 * lifecycle surface (list + detail card + approval widget + progress widget +
 * create modal) is 600+ lines and belongs semantically to rollouts, not to
 * artifact CRUD.
 *
 * Layout:
 *   1. Toolbar with status filter + "Neuer Rollout anlegen" button.
 *   2. Rollout list.
 *   3. Click on a row expands the detail card: bundle, target resolution,
 *      approval widget, phased progress with per-status counts.
 *
 * State machine mirror:
 *   Draft → PendingApproval (Submit) → Approved (Start) → Running →
 *   PausedByHealth ↔ Running (Resume via Start on PausedByHealth)
 *                                 → Completed / Stopped / Failed (terminal).
 *
 * Ops-alarm banner: when a rollout is PausedByHealth, the detail card shows
 * a prominent yellow banner with the auto-stop reason. Deliberate visual
 * weight — this is the moment a human needs to look.
 */

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import type {
  ArtifactDto,
  ListArtifactsResponse,
  ListRolloutsResponse,
  PlantOverviewClientDto,
  ListPlantsOverviewResponse,
  RolloutDto,
  RolloutProgressResponse,
  RolloutStatus,
  RolloutTargetSelectorDto,
  RolloutBundleEntryDto,
  RolloutPhaseDto,
} from '../api/types';
import './RolloutsPage.css';

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
interface BtnProps {
  variant?: BtnVariant;
  size?: 'sm' | 'md';
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  children?: ComponentChildren;
}
function Btn({ variant = 'secondary', size = 'md', onClick, disabled, type = 'button', children }: BtnProps) {
  const classes = ['hv-btn', `hv-btn--${variant}`, `hv-btn--${size}`, disabled ? 'hv-btn--disabled' : '']
    .filter(Boolean).join(' ');
  return <button type={type} class={classes} onClick={onClick} disabled={disabled}>{children}</button>;
}

const STATUSES: RolloutStatus[] = [
  'Draft', 'PendingApproval', 'Approved', 'Running',
  'PausedByHealth', 'Stopped', 'Completed', 'Failed',
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function statusLabel(s: RolloutStatus): string {
  switch (s) {
    case 'Draft': return 'Entwurf';
    case 'PendingApproval': return 'Warten auf Freigabe';
    case 'Approved': return 'Freigegeben (wartet auf Runner)';
    case 'Running': return 'Läuft';
    case 'PausedByHealth': return 'PAUSIERT — Ops-Aufmerksamkeit';
    case 'Stopped': return 'Manuell gestoppt';
    case 'Completed': return 'Abgeschlossen';
    case 'Failed': return 'Fehlgeschlagen';
    default: return s;
  }
}

function statusTone(s: RolloutStatus): 'ok' | 'warn' | 'error' | 'info' | 'muted' {
  switch (s) {
    case 'Running': case 'Approved': return 'info';
    case 'Completed': return 'ok';
    case 'PausedByHealth': return 'warn';
    case 'Failed': return 'error';
    case 'Stopped': case 'Draft': case 'PendingApproval': return 'muted';
    default: return 'muted';
  }
}

export function RolloutsTab() {
  const [refreshTick, setRefreshTick] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RolloutStatus | 'ALL'>('ALL');
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const listQuery = statusFilter === 'ALL'
    ? '/api/rollouts'
    : `/api/rollouts?status=${encodeURIComponent(statusFilter)}`;
  const list = useAsync(
    (signal) => api.get<ListRolloutsResponse>(listQuery, signal),
    [refreshTick, statusFilter],
  );

  const refresh = () => setRefreshTick((t) => t + 1);

  return (
    <div class="hv-rollouts">
      <div class="hv-rollouts__toolbar">
        <div class="hv-rollouts__toolbar-hint">
          Phasen-Rollouts mit Approval-Gate, Kanarien-Regel und Auto-Stopp bei Failures.
        </div>
        <div class="hv-rollouts__filter">
          <label>
            Status:
            <select value={statusFilter} onChange={(e) => setStatusFilter((e.currentTarget as HTMLSelectElement).value as RolloutStatus | 'ALL')}>
              <option value="ALL">Alle</option>
              {STATUSES.map((s) => (
                <option value={s}>{statusLabel(s)}</option>
              ))}
            </select>
          </label>
        </div>
        <Btn variant="primary" onClick={() => setCreateOpen(true)}>
          Neuen Rollout anlegen
        </Btn>
      </div>

      <section class="hv-rollouts__list">
        {list.status === 'loading' && <div class="hv-rollouts__msg">Lade Rollouts…</div>}
        {list.status === 'error' && (
          <div class="hv-rollouts__msg hv-rollouts__msg--error">
            Konnte Rollouts nicht laden: {(list.error as Error)?.message ?? 'Unbekannter Fehler.'}
          </div>
        )}
        {list.status === 'ok' && list.data.rollouts.length === 0 && (
          <div class="hv-rollouts__msg">Keine Rollouts im gewählten Status.</div>
        )}
        {list.status === 'ok' && list.data.rollouts.map((r) => (
          <RolloutRow
            key={r.id}
            rollout={r}
            expanded={expandedId === r.id}
            onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
            onChanged={refresh}
          />
        ))}
      </section>

      {createOpen && (
        <CreateRolloutModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            refresh();
            setCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}

// --- Row + detail ----------------------------------------------------------

function RolloutRow({
  rollout,
  expanded,
  onToggle,
  onChanged,
}: {
  rollout: RolloutDto;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const tone = statusTone(rollout.status);
  return (
    <div class={`hv-rollouts__row hv-rollouts__row--${tone}`}>
      <button type="button" class="hv-rollouts__row-header" onClick={onToggle}>
        <span class="hv-rollouts__row-title">{rollout.displayName}</span>
        <span class={`hv-rollouts__badge hv-rollouts__badge--${tone}`}>
          {statusLabel(rollout.status)}
        </span>
        <span class="hv-rollouts__row-meta">
          {rollout.resolvedTargetCount} Ziele · {rollout.bundle.length} Artefakte · {rollout.phases.length} Phasen
        </span>
        <span class="hv-rollouts__row-date">{formatDate(rollout.createdAt)}</span>
      </button>
      {expanded && (
        <div class="hv-rollouts__row-detail">
          <RolloutDetail rollout={rollout} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

function RolloutDetail({ rollout, onChanged }: { rollout: RolloutDto; onChanged: () => void }) {
  const [progressTick, setProgressTick] = useState(0);
  const isLive = rollout.status === 'Running' || rollout.status === 'PausedByHealth' || rollout.status === 'Approved';
  useEffect(() => {
    if (!isLive) return;
    // Poll progress every 3 s while the rollout is live. Idle rollouts skip
    // the poll to keep the browser quiet.
    const t = window.setInterval(() => setProgressTick((n) => n + 1), 3000);
    return () => window.clearInterval(t);
  }, [isLive]);
  const progress = useAsync(
    (signal) => api.get<RolloutProgressResponse>(`/api/rollouts/${rollout.id}/progress`, signal),
    [rollout.id, progressTick],
  );

  return (
    <>
      {rollout.status === 'PausedByHealth' && (
        <div class="hv-rollouts__alarm">
          <strong>Rollout pausiert.</strong>
          <div>{rollout.statusReason ?? 'Kein Grund gemeldet.'}</div>
          <div class="hv-rollouts__alarm-hint">
            Nach Prüfung: „Fortfahren" ruft /start erneut auf und übergibt den Runner wieder.
          </div>
        </div>
      )}

      <div class="hv-rollouts__detail-grid">
        <div>
          <h4>Bundle</h4>
          <ol class="hv-rollouts__bundle">
            {rollout.bundle.map((b) => (
              <li>
                <span class="hv-rollouts__mono">{b.artifactId.slice(0, 8)}…</span>
                <span>Reihenfolge {b.order}</span>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <h4>Zielauswahl</h4>
          <div class="hv-rollouts__selector">
            {rollout.targetSelector.clientIds && rollout.targetSelector.clientIds.length > 0 && (
              <div>Clients direkt: {rollout.targetSelector.clientIds.length}</div>
            )}
            {rollout.targetSelector.plantIds && rollout.targetSelector.plantIds.length > 0 && (
              <div>Anlagen: {rollout.targetSelector.plantIds.length}</div>
            )}
            <div class="hv-rollouts__resolved">
              Aufgelöst: <strong>{rollout.resolvedTargetCount}</strong> Clients
            </div>
          </div>
        </div>
        <div>
          <h4>Phasen</h4>
          <ol class="hv-rollouts__phases">
            {rollout.phases.map((p, idx) => (
              <li class={idx === rollout.currentPhaseIndex && isLive ? 'hv-rollouts__phase--current' : ''}>
                {p.percent}% · {p.safetyMinutes} min Safety
              </li>
            ))}
          </ol>
          <div class="hv-rollouts__phase-meta">
            Max Failure-Rate: {rollout.maxFailurePercentPerPhase}%
          </div>
        </div>
        <div>
          <h4>Freigaben</h4>
          <ApprovalWidget rollout={rollout} onChanged={onChanged} />
        </div>
      </div>

      {isLive && progress.status === 'ok' && (
        <ProgressWidget progress={progress.data} bundleLength={rollout.bundle.length} />
      )}

      <div class="hv-rollouts__actions">
        <RolloutActions rollout={rollout} onChanged={onChanged} />
      </div>
    </>
  );
}

function ApprovalWidget({ rollout, onChanged }: { rollout: RolloutDto; onChanged: () => void }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const canApprove = rollout.status === 'PendingApproval' || rollout.status === 'Approved';
  const already = rollout.approvals.length; // heuristic, actual "did I approve" needs backend fold
  const need = Math.max(0, rollout.requiredApprovals - already);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/rollouts/${rollout.id}/approve`, { comment: comment.trim() || undefined });
      showToast({ tone: 'success', title: 'Freigabe erfasst', message: 'Approval wurde gespeichert.', autoHideAfterMs: 3000 });
      setComment('');
      onChanged();
    } catch (err) {
      const message = err instanceof ApiError ? `HTTP ${err.status}: ${err.message}` : (err as Error).message;
      showToast({ tone: 'error', title: 'Approve fehlgeschlagen', message, autoHideAfterMs: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="hv-rollouts__approval">
      <div class="hv-rollouts__approval-count">
        <strong>{rollout.approvals.length}</strong> von <strong>{rollout.requiredApprovals}</strong> Freigaben erfasst.
      </div>
      {rollout.approvals.length > 0 && (
        <ul class="hv-rollouts__approval-list">
          {rollout.approvals.map((a) => (
            <li>
              <span class="hv-rollouts__mono">{a.approvedByUserId.slice(0, 8)}…</span>
              <span>{formatDate(a.approvedAt)}</span>
              {a.comment && <span class="hv-rollouts__approval-comment">„{a.comment}"</span>}
            </li>
          ))}
        </ul>
      )}
      {canApprove && need > 0 && (
        <div class="hv-rollouts__approve-form">
          <input
            type="text"
            placeholder="Kommentar (optional)"
            value={comment}
            onInput={(e) => setComment((e.currentTarget as HTMLInputElement).value)}
          />
          <Btn variant="primary" size="sm" onClick={submit} disabled={busy}>
            {busy ? 'Speichere…' : 'Approve'}
          </Btn>
        </div>
      )}
      {canApprove && need === 0 && (
        <div class="hv-rollouts__approval-ready">
          Ausreichend Freigaben — kann gestartet werden.
        </div>
      )}
    </div>
  );
}

function ProgressWidget({ progress, bundleLength }: { progress: RolloutProgressResponse; bundleLength: number }) {
  const c = progress.statusCounts;
  return (
    <div class="hv-rollouts__progress">
      <div class="hv-rollouts__progress-head">
        <strong>Fortschritt Schritt {progress.currentStepIndex + 1} von {bundleLength}</strong>
        <span>Phase {progress.currentPhaseIndex + 1} · gestartet {formatDate(progress.currentPhaseStartedAt)}</span>
      </div>
      <div class="hv-rollouts__counts">
        <span class="hv-rollouts__count hv-rollouts__count--ok">
          {c.applied} <em>applied</em>
        </span>
        <span class="hv-rollouts__count hv-rollouts__count--error">
          {c.failed + c.rejectedSignatureInvalid} <em>failed</em>
        </span>
        <span class="hv-rollouts__count">
          {c.queued + c.downloading + c.verifying + c.applying + c.rebooting} <em>in Arbeit</em>
        </span>
        <span class="hv-rollouts__count hv-rollouts__count--total">
          {c.total} <em>total</em>
        </span>
      </div>
      <div class="hv-rollouts__phasebar">
        {c.applied > 0 && <span class="hv-rollouts__phasebar-slice hv-rollouts__phasebar-slice--ok" style={{ flex: c.applied }} />}
        {c.failed + c.rejectedSignatureInvalid > 0 && <span class="hv-rollouts__phasebar-slice hv-rollouts__phasebar-slice--error" style={{ flex: c.failed + c.rejectedSignatureInvalid }} />}
        {(c.queued + c.downloading + c.verifying + c.applying + c.rebooting) > 0 && (
          <span class="hv-rollouts__phasebar-slice hv-rollouts__phasebar-slice--pending" style={{ flex: c.queued + c.downloading + c.verifying + c.applying + c.rebooting }} />
        )}
      </div>
    </div>
  );
}

function RolloutActions({ rollout, onChanged }: { rollout: RolloutDto; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const call = async (verb: string, path: string) => {
    setBusy(verb);
    try {
      await api.post(path);
      showToast({ tone: 'success', title: verb, message: 'OK', autoHideAfterMs: 2000 });
      onChanged();
    } catch (err) {
      const message = err instanceof ApiError ? `HTTP ${err.status}: ${err.message}` : (err as Error).message;
      showToast({ tone: 'error', title: `${verb} fehlgeschlagen`, message, autoHideAfterMs: 6000 });
    } finally {
      setBusy(null);
    }
  };

  const canDelete =
    rollout.status !== 'Running' && rollout.status !== 'PausedByHealth';

  const deleteRollout = async () => {
    if (!window.confirm(
      `Rollout "${rollout.displayName}" wirklich löschen?\n\n`
      + `Approvals und Target-Reports werden mit weggeräumt. Läuft der Rollout gerade, lehnt der Server ab — dann erst Stop.`
    )) return;
    setBusy('Löschen');
    try {
      await api.delete<void>(`/api/artifact-rollouts/${rollout.id}`);
      showToast({ tone: 'success', title: 'Rollout gelöscht', message: rollout.displayName, autoHideAfterMs: 3000 });
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError
        ? ((err.body as { error?: string })?.error ?? `HTTP ${err.status}: ${err.message}`)
        : (err as Error).message;
      showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {rollout.status === 'Draft' && (
        <Btn variant="secondary" size="sm" onClick={() => call('Submit', `/api/rollouts/${rollout.id}/submit-for-approval`)} disabled={busy !== null}>
          {busy === 'Submit' ? '…' : 'Zur Freigabe einreichen'}
        </Btn>
      )}
      {(rollout.status === 'PendingApproval' || rollout.status === 'Approved' || rollout.status === 'PausedByHealth') && (
        <Btn variant="primary" size="sm" onClick={() => call('Start', `/api/rollouts/${rollout.id}/start`)} disabled={busy !== null}>
          {busy === 'Start' ? '…' : rollout.status === 'PausedByHealth' ? 'Fortfahren' : 'Starten'}
        </Btn>
      )}
      {rollout.status !== 'Completed' && rollout.status !== 'Stopped' && rollout.status !== 'Failed' && (
        <Btn variant="danger" size="sm" onClick={() => call('Stop', `/api/rollouts/${rollout.id}/stop`)} disabled={busy !== null}>
          {busy === 'Stop' ? '…' : 'Stop'}
        </Btn>
      )}
      {canDelete && (
        <Btn variant="danger" size="sm" onClick={deleteRollout} disabled={busy !== null}>
          {busy === 'Löschen' ? 'Lösche…' : 'Löschen'}
        </Btn>
      )}
    </>
  );
}

// --- Create modal ----------------------------------------------------------

function CreateRolloutModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [selectedArtifacts, setSelectedArtifacts] = useState<string[]>([]);
  const [selectedPlantIds, setSelectedPlantIds] = useState<string[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [phases, setPhases] = useState<RolloutPhaseDto[]>([
    { percent: 5, safetyMinutes: 60 },
    { percent: 25, safetyMinutes: 60 },
    { percent: 100, safetyMinutes: 60 },
  ]);
  const [maxFailure, setMaxFailure] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  const artifacts = useAsync(
    (signal) => api.get<ListArtifactsResponse>('/api/artifacts', signal),
    [],
  );
  const plantsQuery = useAsync(
    (signal) => api.get<ListPlantsOverviewResponse>('/api/plants/overview?pageSize=1000', signal),
    [],
  );

  const toggle = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const submit = async () => {
    if (!displayName.trim() || selectedArtifacts.length === 0) return;
    if (selectedPlantIds.length === 0 && selectedClientIds.length === 0) return;
    setSubmitting(true);
    try {
      const bundle: RolloutBundleEntryDto[] = selectedArtifacts.map((id, idx) => ({ artifactId: id, order: idx }));
      const targetSelector: RolloutTargetSelectorDto = {
        clientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined,
        plantIds: selectedPlantIds.length > 0 ? selectedPlantIds : undefined,
      };
      await api.post('/api/rollouts', {
        displayName: displayName.trim(),
        bundle,
        targetSelector,
        phases,
        maxFailurePercentPerPhase: maxFailure,
      });
      showToast({ tone: 'success', title: 'Rollout angelegt', message: `„${displayName}" wurde als Entwurf gespeichert.`, autoHideAfterMs: 4000 });
      onCreated();
    } catch (err) {
      const message = err instanceof ApiError ? `HTTP ${err.status}: ${JSON.stringify(err.body)}` : (err as Error).message;
      showToast({ tone: 'error', title: 'Rollout anlegen fehlgeschlagen', message, autoHideAfterMs: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="hv-rollouts-modal" onClick={onClose}>
      <div class="hv-rollouts-modal__box" onClick={(e) => e.stopPropagation()}>
        <div class="hv-rollouts-modal__head">
          <h3>Neuen Rollout anlegen</h3>
          <button type="button" class="hv-rollouts-modal__close" onClick={onClose}>×</button>
        </div>
        <div class="hv-rollouts-modal__body">
          <label>Name
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.currentTarget as HTMLInputElement).value)} />
          </label>

          <fieldset>
            <legend>Bundle (Reihenfolge folgt Auswahl)</legend>
            {artifacts.status === 'loading' && <div>Lade Artefakte…</div>}
            {artifacts.status === 'ok' && (
              <div class="hv-rollouts-modal__artifacts">
                {artifacts.data.artifacts.map((a: ArtifactDto) => (
                  <label key={a.id} class="hv-rollouts-modal__row">
                    <input
                      type="checkbox"
                      checked={selectedArtifacts.includes(a.id)}
                      onChange={() => setSelectedArtifacts(toggle(selectedArtifacts, a.id))}
                    />
                    <span>{a.displayName} <em>{a.version}</em> <span class="hv-rollouts__mono">{a.kind}</span></span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend>Zielauswahl</legend>
            <div class="hv-rollouts-modal__targets">
              <div>
                <strong>Anlagen (alle Clients der Anlage)</strong>
                {plantsQuery.status === 'ok' && (
                  <div class="hv-rollouts-modal__plants">
                    {plantsQuery.data.plants.map((p) => (
                      <label key={p.id} class="hv-rollouts-modal__row">
                        <input
                          type="checkbox"
                          checked={selectedPlantIds.includes(p.id)}
                          onChange={() => setSelectedPlantIds(toggle(selectedPlantIds, p.id))}
                        />
                        <span>{p.displayName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <strong>Einzelne Clients direkt (optional)</strong>
                {plantsQuery.status === 'ok' && (
                  <div class="hv-rollouts-modal__plants">
                    {plantsQuery.data.plants.flatMap((p) => (p.clients ?? []) as PlantOverviewClientDto[]).map((c) => (
                      <label key={c.id} class="hv-rollouts-modal__row">
                        <input
                          type="checkbox"
                          checked={selectedClientIds.includes(c.id)}
                          onChange={() => setSelectedClientIds(toggle(selectedClientIds, c.id))}
                        />
                        <span>{c.displayName ?? c.mqttClientName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Phasen</legend>
            <div class="hv-rollouts-modal__phases">
              {phases.map((p, idx) => (
                <div class="hv-rollouts-modal__phase-row">
                  <span>Phase {idx + 1}:</span>
                  <label>
                    Prozent
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={p.percent}
                      onInput={(e) => {
                        const val = Number((e.currentTarget as HTMLInputElement).value);
                        setPhases(phases.map((ph, i) => i === idx ? { ...ph, percent: val } : ph));
                      }}
                    />
                  </label>
                  <label>
                    Safety-Minuten
                    <input
                      type="number"
                      min={0}
                      value={p.safetyMinutes}
                      onInput={(e) => {
                        const val = Number((e.currentTarget as HTMLInputElement).value);
                        setPhases(phases.map((ph, i) => i === idx ? { ...ph, safetyMinutes: val } : ph));
                      }}
                    />
                  </label>
                  {phases.length > 1 && (
                    <button type="button" class="hv-rollouts-modal__mini" onClick={() => setPhases(phases.filter((_, i) => i !== idx))}>
                      entfernen
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                class="hv-rollouts-modal__mini"
                onClick={() => setPhases([...phases, { percent: 100, safetyMinutes: 60 }])}
              >
                + Phase
              </button>
            </div>
            <div class="hv-rollouts-modal__hint">
              Regel: Prozent aufsteigend, letzte Phase = 100. Kanarien-Regel überschreibt Phase 1
              automatisch auf 1 Panel, wenn das Bundle mind. ein Artefakt enthält, das den Client
              neustartet (Android APK, Android System OTA).
            </div>
          </fieldset>

          <label>Max Failure-Rate pro Phase (%)
            <input type="number" min={0} max={100} value={maxFailure} onInput={(e) => setMaxFailure(Number((e.currentTarget as HTMLInputElement).value))} />
          </label>
        </div>

        <div class="hv-rollouts-modal__actions">
          <Btn variant="ghost" onClick={onClose}>Abbrechen</Btn>
          <Btn variant="primary" onClick={submit} disabled={submitting || !displayName.trim() || selectedArtifacts.length === 0 || (selectedPlantIds.length === 0 && selectedClientIds.length === 0)}>
            {submitting ? 'Anlegen…' : 'Als Entwurf anlegen'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
